import { useIntervalFn } from '@vueuse/core'

// ──────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────

export interface AnswerRevealState {
  scramble: boolean
  swordDrill: boolean
  trivia: boolean
}
export interface CategoriesItem { category: string }
export interface ChallengeWheelConfig {
  customTeamCount: number
  customTeamNames: string[]
  miniGameLabels: Record<MiniGame, string>
  selectedGames: MiniGame[]
  teamStructure: TeamStructure
  theme: Theme
}
export interface ChallengeWheelSyncState {
  answerRevealed: AnswerRevealState
  config: ChallengeWheelConfig
  currentChallenge: CurrentChallenge | null
  currentSegmentIndex: null | number
  phase: GamePhase
  revealCountdown: number
  roundPointAwarded: boolean
  teams: Team[]
  timerPaused: boolean
  timerSeconds: number
  timesUp: boolean
  wheelRotation: number
}

export type ContentItem = CategoriesItem | SwordDrillItem | TriviaItem | WordScrambleItem

export interface CurrentChallenge {
  content: ContentItem
  game: MiniGame
}

export type GamePhase = 'idle' | 'playing' | 'revealing' | 'setup' | 'spinning'

export type MiniGame = 'bible-trivia' | 'categories' | 'sword-drill' | 'word-scramble'
export interface SwordDrillItem { reference: string, text: string }

export interface Team {
  color: string
  name: string
  score: number
}

export type TeamStructure = 'boys-v-girls' | 'color-teams' | 'custom'

export type Theme = 'bible' | 'christmas' | 'easter' | 'general' | 'thanksgiving'

export interface ThemeContent {
  bible_trivia: TriviaItem[]
  categories: CategoriesItem[]
  sword_drill: SwordDrillItem[]
  theme: Theme
  word_scramble: WordScrambleItem[]
}
export interface TriviaItem { answer: string, question: string }

export interface WheelSegment {
  color: string
  label: string
  type: MiniGame
}

export interface WordScrambleItem { hint?: string, word: string }

// ──────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────

const MIN_SEGMENTS = 12

export const COLOR_TEAMS: Team[] = [
  { color: '#ef4444', name: 'Red', score: 0 },
  { color: '#3b82f6', name: 'Blue', score: 0 },
  { color: '#22c55e', name: 'Green', score: 0 },
  { color: '#eab308', name: 'Yellow', score: 0 },
]

export const BOYS_GIRLS_TEAMS: Team[] = [
  { color: '#3b82f6', name: 'Boys', score: 0 },
  { color: '#ec4899', name: 'Girls', score: 0 },
]

export const CUSTOM_COLORS = [ '#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#f97316' ] as const

export const MINI_GAME_CONFIG: Record<MiniGame, { color: string, duration: number }> = {
  'bible-trivia': { color: '#3b82f6', duration: 30 },
  'categories': { color: '#10b981', duration: 0 },
  'sword-drill': { color: '#f59e0b', duration: 30 },
  'word-scramble': { color: '#8b5cf6', duration: 30 },
}

// ──────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────

function buildSegments(
  selectedGames: MiniGame[],
  miniGameLabels: Record<MiniGame, string>,
): WheelSegment[] {
  if (selectedGames.length === 0) {
    return []
  }
  const perGame = Math.ceil(MIN_SEGMENTS / selectedGames.length)
  const segments: WheelSegment[] = []
  for (let i = 0; i < perGame; i++) {
    for (const game of selectedGames) {
      segments.push({
        color: MINI_GAME_CONFIG[game].color,
        label: miniGameLabels[game] ?? game,
        type: game,
      })
    }
  }
  return segments
}

function cloneForSync<T>(value: T): T {
  const rawValue = toRaw(value) as unknown

  if (rawValue === null || typeof rawValue !== 'object') {
    return rawValue as T
  }

  if (Array.isArray(rawValue)) {
    return rawValue.map(item => cloneForSync(item)) as T
  }

  const cloned: Record<string, unknown> = {}
  for (const [ key, nestedValue ] of Object.entries(rawValue as Record<string, unknown>)) {
    cloned[key] = cloneForSync(nestedValue)
  }
  return cloned as T
}

// ──────────────────────────────────────────────────────────
// Store
// ──────────────────────────────────────────────────────────

export const useChallengeWheelStore = defineStore('challengeWheel', () => {
  // ── Phase ──────────────────────────────────────────────
  const phase = ref<GamePhase>('setup')

  // ── Content (set from the page after loading) ──────────
  const themeContent = ref<null | ThemeContent>(null)

  // ── Teams ──────────────────────────────────────────────
  const teams = ref<Team[]>([])

  // ── Setup config ──────────────────────────────────────
  const config = ref<ChallengeWheelConfig>({
    customTeamCount: 4,
    customTeamNames: [ 'Team 1', 'Team 2', 'Team 3', 'Team 4', 'Team 5', 'Team 6' ],
    miniGameLabels: {
      'bible-trivia': '',
      'categories': '',
      'sword-drill': '',
      'word-scramble': '',
    },
    selectedGames: [ 'sword-drill', 'bible-trivia', 'categories', 'word-scramble' ],
    teamStructure: 'color-teams',
    theme: 'bible',
  })

  // ── Wheel ──────────────────────────────────────────────
  const wheelRotation = ref(0)
  const currentSegmentIndex = ref<null | number>(null)

  // ── Current round ──────────────────────────────────────
  const currentChallenge = ref<CurrentChallenge | null>(null)
  const revealCountdown = ref(3)

  const answerRevealed = ref<AnswerRevealState>({
    scramble: false,
    swordDrill: false,
    trivia: false,
  })

  // ── Timer ──────────────────────────────────────────────
  const timerSeconds = ref(0)
  const timerPaused = ref(false)

  // ── Content deck tracking (pre-shuffled, no-repeat queues) ──────────
  // Each game gets a Fisher-Yates-shuffled queue of indices that drains fully
  // before reshuffling so every item is seen exactly once per cycle.
  const contentQueues = ref<Partial<Record<MiniGame, number[]>>>({})
  const lastPickedIndex = ref<Partial<Record<MiniGame, number>>>({})

  // Track the last 2 landed game types to prevent 3 in a row
  const recentGames = ref<MiniGame[]>([])

  // Whether a positive point was awarded in the current playing round
  const roundPointAwarded = ref(false)

  // Time's Up overlay state
  const timesUp = ref(false)
  let timesUpTimeout: null | ReturnType<typeof setTimeout> = null

  // ── Computed ───────────────────────────────────────────
  const wheelSegments = computed(() =>
    buildSegments(config.value.selectedGames, config.value.miniGameLabels),
  )

  const currentMiniGameDuration = computed(() =>
    currentChallenge.value ? MINI_GAME_CONFIG[currentChallenge.value.game].duration : 0,
  )

  // ── Timer interval (client-side only) ─────────────────
  const { pause: pauseTimer, resume: resumeTimer } = useIntervalFn(() => {
    if (phase.value === 'playing' && !timerPaused.value && timerSeconds.value > 0) {
      timerSeconds.value -= 1
      if (timerSeconds.value === 0) {
        // Don't auto-close — show a Time's Up overlay then auto-clear after 3 s
        pauseTimer()
        timesUp.value = true
        timesUpTimeout = setTimeout(() => {
          timesUp.value = false
          revealAnswerForCurrentChallenge()
          timesUpTimeout = null
        }, 1800)
      }
    }
  }, 1000, { immediate: false })

  // Reveal countdown — plain setInterval to avoid extra ticking
  let revealTimer: null | ReturnType<typeof setInterval> = null

  // ── Actions ────────────────────────────────────────────

  function setContent(content: ThemeContent) {
    themeContent.value = content
  }

  function setMiniGameLabels(labels: Record<MiniGame, string>) {
    config.value.miniGameLabels = labels
  }

  function startGame() {
    const structure = config.value.teamStructure
    if (structure === 'color-teams') {
      teams.value = COLOR_TEAMS.map(t => ({ ...t }))
    } else if (structure === 'boys-v-girls') {
      teams.value = BOYS_GIRLS_TEAMS.map(t => ({ ...t }))
    } else {
      teams.value = Array.from({ length: config.value.customTeamCount }, (_, i) => ({
        color: CUSTOM_COLORS[i % CUSTOM_COLORS.length] ?? '#6b7280',
        name: config.value.customTeamNames[i] || `Team ${i + 1}`,
        score: 0,
      }))
    }
    contentQueues.value = {}
    lastPickedIndex.value = {}
    recentGames.value = []
    currentChallenge.value = null
    timerSeconds.value = 0
    timerPaused.value = false
    resetAnswerRevealState()
    wheelRotation.value = 0
    currentSegmentIndex.value = null
    phase.value = 'idle'
  }

  function exitGame() {
    pauseTimer()
    clearRevealTimer()
    clearTimesUpTimeout()
    resetAnswerRevealState()
    timesUp.value = false
    phase.value = 'setup'
  }

  function spinWheel() {
    if (phase.value !== 'idle') {
      return
    }
    const segments = wheelSegments.value
    if (segments.length === 0) {
      return
    }

    // Crypto-quality random integer in [0, max)
    function cryptoRandInt(max: number): number {
      const arr = new Uint32Array(1)
      crypto.getRandomValues(arr)
      return arr[0]! % max
    }

    // Build candidate list — exclude segments whose game has appeared twice in a row
    const last2Same
      = recentGames.value.length >= 2
        && recentGames.value.at(-1) === recentGames.value.at(-2)
    const blockedGame = last2Same ? recentGames.value.at(-1) : null

    const candidateIndices = blockedGame
      ? segments.map((s, i) => ({ i, s })).filter(({ s }) => s.type !== blockedGame).map(({ i }) => i)
      : segments.map((_, i) => i)

    const pool = candidateIndices.length > 0 ? candidateIndices : segments.map((_, i) => i)
    const targetIndex = pool[cryptoRandInt(pool.length)]!

    const sliceAngle = 360 / segments.length
    const targetCenter = targetIndex * sliceAngle + sliceAngle / 2

    // Accumulate rotation so CSS transition always goes forward
    const currentMod = wheelRotation.value % 360
    const alignDelta = (360 - targetCenter - currentMod + 720) % 360
    const extraRotations = (5 + cryptoRandInt(3)) * 360

    wheelRotation.value += extraRotations + alignDelta
    currentSegmentIndex.value = targetIndex
    phase.value = 'spinning'
  }

  function resolveSpin() {
    if (phase.value !== 'spinning' || currentSegmentIndex.value === null) {
      return
    }
    const segment = wheelSegments.value[currentSegmentIndex.value]
    if (!segment) {
      return
    }

    const content = pickContent(segment.type)
    if (!content) {
      return
    }

    currentChallenge.value = { content, game: segment.type }
    revealCountdown.value = 3
    phase.value = 'revealing'

    // Record the landed game for consecutive-game guard
    recentGames.value = [ ...recentGames.value.slice(-1), segment.type ]

    revealTimer = setInterval(() => {
      revealCountdown.value -= 1
      if (revealCountdown.value <= 0) {
        clearRevealTimer()
        beginMiniGame()
      }
    }, 1000)
  }

  function beginMiniGame() {
    if (!currentChallenge.value) {
      return
    }
    const duration = currentMiniGameDuration.value
    timerSeconds.value = duration
    timerPaused.value = false
    roundPointAwarded.value = false
    resetAnswerRevealState()
    clearTimesUpTimeout()
    timesUp.value = false
    phase.value = 'playing'
    if (duration > 0) {
      resumeTimer()
    }
  }

  function endRound() {
    pauseTimer()
    clearTimesUpTimeout()
    timesUp.value = false
    timerPaused.value = false
    resetAnswerRevealState()
    currentChallenge.value = null
    currentSegmentIndex.value = null
    phase.value = 'idle'
    // Delay the reset so the timer fade-out animation (duration-300) completes
    // before the displayed value snaps to 0.
    setTimeout(() => {
      timerSeconds.value = 0
    }, 350)
  }

  function toggleTimer() {
    if (phase.value !== 'playing' || currentMiniGameDuration.value === 0) {
      return
    }
    timerPaused.value = !timerPaused.value
    if (timerPaused.value) {
      pauseTimer()
    } else {
      resumeTimer()
    }
  }

  function stopTimer() {
    timerPaused.value = true
    pauseTimer()
  }

  function incrementScore(teamIndex: number) {
    const team = teams.value[teamIndex]
    if (team) {
      team.score += 1
      // Auto-end the round once a point is awarded during play
      if (phase.value === 'playing') {
        roundPointAwarded.value = true
        endRound()
      }
    }
  }

  function decrementScore(teamIndex: number) {
    const team = teams.value[teamIndex]
    if (team && team.score > 0) {
      team.score -= 1
    }
  }

  // Dismiss the overlay without awarding a point (only if none granted yet)
  function dismissRound() {
    if (phase.value === 'playing' && !roundPointAwarded.value) {
      endRound()
    }
  }

  function revealAnswerForCurrentChallenge() {
    const game = currentChallenge.value?.game
    if (!game) {
      return
    }
    if (game === 'bible-trivia') {
      answerRevealed.value.trivia = true
      return
    }
    if (game === 'word-scramble') {
      answerRevealed.value.scramble = true
      return
    }
    if (game === 'sword-drill') {
      answerRevealed.value.swordDrill = true
    }
  }

  function resetAnswerRevealState() {
    answerRevealed.value = {
      scramble: false,
      swordDrill: false,
      trivia: false,
    }
  }

  function getSyncState(): ChallengeWheelSyncState {
    return {
      answerRevealed: cloneForSync(answerRevealed.value),
      config: cloneForSync(config.value),
      currentChallenge: currentChallenge.value ? cloneForSync(currentChallenge.value) : null,
      currentSegmentIndex: currentSegmentIndex.value,
      phase: phase.value,
      revealCountdown: revealCountdown.value,
      roundPointAwarded: roundPointAwarded.value,
      teams: cloneForSync(teams.value),
      timerPaused: timerPaused.value,
      timerSeconds: timerSeconds.value,
      timesUp: timesUp.value,
      wheelRotation: wheelRotation.value,
    }
  }

  function applySyncState(nextState: ChallengeWheelSyncState) {
    pauseTimer()
    clearRevealTimer()
    clearTimesUpTimeout()

    answerRevealed.value = cloneForSync(nextState.answerRevealed)
    config.value = cloneForSync(nextState.config)
    currentChallenge.value = nextState.currentChallenge ? cloneForSync(nextState.currentChallenge) : null
    currentSegmentIndex.value = nextState.currentSegmentIndex
    phase.value = nextState.phase
    revealCountdown.value = nextState.revealCountdown
    roundPointAwarded.value = nextState.roundPointAwarded
    teams.value = cloneForSync(nextState.teams)
    timerPaused.value = nextState.timerPaused
    timerSeconds.value = nextState.timerSeconds
    timesUp.value = nextState.timesUp
    wheelRotation.value = nextState.wheelRotation
  }

  // ── Internal helpers ──────────────────────────────────

  function clearRevealTimer() {
    if (revealTimer !== null) {
      clearInterval(revealTimer)
      revealTimer = null
    }
  }

  function clearTimesUpTimeout() {
    if (timesUpTimeout !== null) {
      clearTimeout(timesUpTimeout)
      timesUpTimeout = null
    }
  }

  function pickContent(game: MiniGame): ContentItem | null {
    if (!themeContent.value) {
      return null
    }

    const poolMap: Record<MiniGame, ContentItem[]> = {
      'bible-trivia': themeContent.value.bible_trivia,
      'categories': themeContent.value.categories,
      'sword-drill': themeContent.value.sword_drill,
      'word-scramble': themeContent.value.word_scramble,
    }

    const pool = poolMap[game]
    if (!pool || pool.length === 0) {
      return null
    }

    let queue = contentQueues.value[game]

    if (!queue || queue.length === 0) {
      // Build a freshly shuffled deck of all indices (Fisher-Yates)
      const indices = Array.from({ length: pool.length }, (_, i) => i)
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[ indices[i], indices[j] ] = [ indices[j]!, indices[i]! ]
      }
      // Avoid boundary repeat: if the first item in the new deck is the same
      // as the last item served in the previous cycle, swap it with the next.
      const last = lastPickedIndex.value[game]
      if (last !== undefined && indices.length > 1 && indices[0] === last) {
        ;[ indices[0], indices[1] ] = [ indices[1]!, indices[0]! ]
      }
      queue = indices
      contentQueues.value[game] = queue
    }

    const idx = queue.shift()!
    lastPickedIndex.value[game] = idx
    return pool[idx] ?? null
  }

  return {
    answerRevealed,
    applySyncState,
    beginMiniGame,
    config,
    currentChallenge,
    currentMiniGameDuration,
    currentSegmentIndex,
    decrementScore,
    dismissRound,
    endRound,
    exitGame,
    getSyncState,
    incrementScore,
    phase,
    resolveSpin,
    revealAnswerForCurrentChallenge,
    revealCountdown,
    roundPointAwarded,
    setContent,
    setMiniGameLabels,
    spinWheel,
    startGame,
    stopTimer,
    teams,
    themeContent,
    timerPaused,
    timerSeconds,
    timesUp,
    toggleTimer,
    wheelRotation,
    wheelSegments,
  }
})
